import type { ApiEndpointBuilder } from "../apiEndpointHelpers";

/** The dashboards whose Status ▾ / Health ▾ menus show option counts. */
export type AdminV2CrmStateCountSurface =
  "contacts" | "users" | "organisations" | "partners";

/** A saved-Audience tab counts the same bounded members as its list. */
export interface PartnerAudienceCountScope {
  audiencePreview: true;
  audienceOrganisationId?: string;
  q?: string;
  searchScope?: "row";
  scopeSearch?: string;
  scopeSearchScope?: "row" | "support";
  filters?: string;
  matchLogic?: "all" | "any";
  scopeFilters?: string;
  scopeFilterMatchLogic?: "all" | "any";
}

/**
 * `stateCounts`: for each quick-filter field, a count per canonical option.
 * A field the caller may not read (Contacts linked states) is absent.
 */
export type AdminV2CrmStateCounts = Record<string, Record<string, number>>;

/** `null` when the response carried no usable counts (older API). */
export interface AdminV2CrmStateCountsResult {
  stateCounts: AdminV2CrmStateCounts | null;
}

const SURFACE_READS: Record<
  AdminV2CrmStateCountSurface,
  {
    url: string;
    tag:
      | "AdminV2CrmPeople"
      | "AdminV2CrmUsers"
      | "AdminV2CrmOrganisations"
      | "AdminV2CrmFirms";
  }
> = {
  contacts: { url: "/admin/v2/crm/people", tag: "AdminV2CrmPeople" },
  users: { url: "/admin/v2/crm/users", tag: "AdminV2CrmUsers" },
  organisations: {
    url: "/admin/v2/crm/organisations",
    tag: "AdminV2CrmOrganisations",
  },
  partners: { url: "/admin/v2/crm/firms", tag: "AdminV2CrmFirms" },
};

const isCountRecord = (value: unknown): value is Record<string, number> =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  Object.values(value).every(
    (count) =>
      typeof count === "number" && Number.isInteger(count) && count >= 0,
  );

/** Keeps only well-formed fields, so a malformed one shows no numbers. */
export const toAdminV2CrmStateCountsResult = (
  response: unknown,
): AdminV2CrmStateCountsResult => {
  const raw =
    typeof response === "object" && response !== null
      ? (response as { stateCounts?: unknown }).stateCounts
      : undefined;
  if (typeof raw !== "object" || raw === null || Array.isArray(raw))
    return { stateCounts: null };
  return {
    stateCounts: Object.fromEntries(
      Object.entries(raw).filter(([, counts]) => isCountRecord(counts)),
    ),
  };
};

/**
 * Status/Health quick-filter option counts (backend Milestones 40 and 41;
 * Batch 11 Users row 31, Organisations and Partners row 30, Contacts rows
 * 31–32).
 *
 * @remarks
 * The counts ignore search, filters and paging ("Option counts are taken
 * across all users"), so one cached read per dashboard serves every filter
 * state; a one-row page keeps the payload small. Contacts counts cover the
 * active view only (`activeOnly=true`), as the server computes them nowhere
 * else. Each read hydrates the caller's whole collection, so callers fetch it
 * when a menu is about to open rather than on page load. The list tags make
 * any change to the records refetch open counts.
 */
export const buildAdminV2CrmStateCountEndpoints = (
  builder: ApiEndpointBuilder,
) => ({
  getAdminV2CrmStateCounts: builder.query<
    AdminV2CrmStateCountsResult,
    | AdminV2CrmStateCountSurface
    | {
        surface: "partners";
        scope: PartnerAudienceCountScope;
      }
  >({
    query: (arg) => {
      const surface = typeof arg === "string" ? arg : arg.surface;
      const scope = typeof arg === "string" ? {} : arg.scope;
      return {
        url: SURFACE_READS[surface].url,
        params: {
          includeStateCounts: true,
          ...(surface === "contacts" ? { activeOnly: true } : {}),
          ...scope,
          limit: 1,
          offset: 0,
        },
      };
    },
    transformResponse: (response: unknown) =>
      toAdminV2CrmStateCountsResult(response),
    // No list tags: every bulk action invalidates the list, and re-running
    // this whole-collection read in the background each time is too costly.
    // The menus refresh a read older than a minute when they next open.
    keepUnusedDataFor: 60,
  }),
});
